import * as yup from 'yup';
import { ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { ePartyType } from '../../../ui-toolkit/enums/ePartyType';
import { AddressSchemaGenerator } from './AddressSchema';
import { DateTime } from 'luxon';
import { eEntityType } from '../../../ui-toolkit/enums/eEntityType';

export const SignatorySchemaGenerator = () =>
  yup.object({
    id: yup.string().required().nullable(),
    firstName: yup.string().required().nullable(),
    middleName: yup.string().notRequired().nullable(),
    lastName: yup.string().required().nullable(),
    email: yup.string().email().nullable().notRequired(),
    phoneNumber: yup.string().nullable().notRequired(),
    address: AddressSchemaGenerator().default(undefined).nullable(),
  });

export const PartySchemaGenerator = () =>
  yup.object({
    id: yup.string().required(),
    type: yup.mixed<ePartyType>().required().oneOf([ePartyType.person, ePartyType.entity]),
    purpose: yup
      .mixed<ePartyPurpose>()
      .required()
      .oneOf([ePartyPurpose.seller, ePartyPurpose.buyer, ePartyPurpose.borrower]),
    firstName: yup.string().nullable().when('type', {
      is: ePartyType.person,
      then: yup.string().required().nullable(),
      otherwise: yup.string().notRequired().nullable(),
    }),
    middleName: yup.string().nullable().notRequired(),
    lastName: yup.string().nullable().when('type', {
      is: ePartyType.person,
      then: yup.string().required().nullable(),
      otherwise: yup.string().notRequired().nullable(),
    }),
    email: yup.string().notRequired().nullable().when('type', {
      is: ePartyType.person,
      then: yup.string().email().nullable(),
      otherwise: yup.string().nullable(),
    }),
    ssn: yup
      .string()
      .notRequired()
      .nullable()
      .when('type', {
        is: ePartyType.person,
        then: yup.string().min(4).max(4).nullable(),
        otherwise: yup.string().nullable(),
      }),
    maritalStatus: yup.string().notRequired().nullable(),
    phoneNumber: yup
      .string()
      .notRequired()
      .nullable()
      .when('type', {
        is: ePartyType.person,
        then: yup.string().min(10).max(15).nullable(),
        otherwise: yup.string().nullable(),
      }),
    entityName: yup.string().nullable().when('type', {
      is: ePartyType.entity,
      then: yup.string().required().nullable(),
      otherwise: yup.string().notRequired().nullable(),
    }),
    entityType: yup
      .mixed<eEntityType>()
      .nullable()
      .when('type', {
        is: ePartyType.entity,
        then: yup
          .mixed<eEntityType>()
          .oneOf(
            [
              eEntityType.corporation,
              eEntityType.estate,
              eEntityType.joint_venture,
              eEntityType.llc,
              eEntityType.non_profit_corporation,
              eEntityType.partnership,
              eEntityType.trust,
              eEntityType.other,
            ],
            'This is required'
          )
          .required(),
        otherwise: yup.mixed<eEntityType>().notRequired().nullable(),
      }),
    signatories: yup.array(SignatorySchemaGenerator()),
    address: AddressSchemaGenerator().default(undefined).nullable(),
    dateOfBirth: yup
      .string()
      .default(undefined)
      .nullable()
      .notRequired()
      .when('type', {
        is: ePartyType.entity,
        then: yup.string().nullable().notRequired(),
        otherwise: yup
          .string()
          .nullable()
          .notRequired()
          .test(
            'dateOfBirth',
            'Enter a valid date',
            (value?: string | null) => !value || DateTime.fromISO(value).isValid
          )
          .test(
            'dateOfBirth-not-future',
            'Date of birth cannot be in the future',
            (value?: string | null) => {
              if (!value) return true;
              const date = DateTime.fromISO(value);
              return date.isValid && date <= DateTime.now().startOf('day');
            }
          ),
      }),
  });

const PartySchema = PartySchemaGenerator();
const SignatorySchema = SignatorySchemaGenerator();

export type IPartySchema = yup.InferType<typeof PartySchema>;
export type ISignatorySchema = yup.InferType<typeof SignatorySchema>;
